import cx from "clsx";
import { Artwork } from "kreate-common/modules/business-types";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import React from "react";
import { MdEdit } from "react-icons/md";

import InputNumEditions$Compact from "../../../../components/InputNumEditions$Compact";
import LabelledValue from "../../../../components/LabelledValue";
import PanelLayout$Emphasis from "../../../../components/PanelLayout$Emphasis";

import { runFlow$MintArtwork } from "./generators/runFlow$MintArtwork";
import { runFlow$UpdateMintingDetails } from "./generators/runFlow$UpdateMintingDetails";
import styles from "./index.module.scss";
import { analyzeArtwork } from "./utils";

import { useBusyCount } from "@/modules/common-hooks/useBusyCount";
import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import {
  assert,
  intentionallyIgnoreError,
} from "@/modules/common-utils/assert";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { InlineError } from "@/modules/form-field/errors";
import { useField$Natural } from "@/modules/form-field/hooks/useField$Natural";
import InlineErrorViewer from "@/modules/kreate-components/components/InlineErrorViewer";
import { useModalTrigger$ModalAsyncGenerator } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import { useModalTrigger$ModalConnectWallet } from "@/modules/kreate-modals/modals/ModalConnectWallet/hooks/useModalTrigger$ModalConnectWallet";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  artwork: Artwork | null | undefined;
};

export default function MethodOnDemandMinting({
  className,
  style,
  artwork,
}: Props) {
  const loginStatus = useLoginStatus();

  const modalTriggers = {
    ModalAsyncGenerator: useModalTrigger$ModalAsyncGenerator(),
    ModalFailure: useModalTrigger$ModalFailure(),
    ModalConnectWallet: useModalTrigger$ModalConnectWallet(),
  };

  const handleError = async (error: unknown) => {
    if (error instanceof InlineError) return;
    shamelesslyRevalidateEverything().catch(intentionallyIgnoreError);
    await modalTriggers.ModalFailure.openModalWithError(error);
  };

  const { busy, withCustomErrorHandler } = useBusyCount();
  const { stats, constraints } = analyzeArtwork(artwork);

  const canUserUpdateMintingDetails =
    loginStatus?.loggedIn &&
    (loginStatus.isAgent || artwork?.createdBy === loginStatus.userId);

  const fieldNumEditions = useField$Natural({
    initialValue: constraints.numMinting.min,
    min: constraints.numMinting.min,
    max: constraints.numMinting.max,
  });

  const handleClickMintNow = withCustomErrorHandler(handleError)(async () => {
    const numMinting = fieldNumEditions.validateBeforeSubmitting();
    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
    assert(artwork?.utxo?.datum.type === "artwork", "not eligible for minting");
    await modalTriggers.ModalAsyncGenerator.openModal(() =>
      runFlow$MintArtwork({
        artwork,
        cip30Wallet,
        numMinting,
      })
    );
  });

  const handleClickUpdateMintingDetails = withCustomErrorHandler(handleError)(
    async () => {
      const cip30Wallet =
        await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
      assert(
        artwork?.utxo?.datum.type === "artwork",
        "not eligible for minting"
      );
      const datumArtwork = artwork.utxo.datum.value;
      await modalTriggers.ModalAsyncGenerator.openModal(() =>
        runFlow$UpdateMintingDetails({
          initialMintPrice: datumArtwork.mintFee,
          initialNumEditions: datumArtwork.numEditions,
          cip30Wallet,
          artwork,
        })
      );
    }
  );

  return (
    <div className={cx(styles.container, className)} style={style}>
      <PanelLayout$Emphasis.Root>
        <PanelLayout$Emphasis.Main>
          <Flex.Row gap="16px 32px" flexWrap="wrap">
            <LabelledValue
              label="Price"
              value={stats.price.text}
              title={stats.price.tooltip}
              slotAfterValue={
                !!canUserUpdateMintingDetails && (
                  <ButtonTransparentV2
                    icon={<MdEdit size="16px" />}
                    onClick={handleClickUpdateMintingDetails}
                    smart={false}
                  />
                )
              }
            />
            <LabelledValue
              label="Minted"
              value={stats.minted.text}
              title={stats.minted.tooltip}
              slotAfterValue={
                !!canUserUpdateMintingDetails && (
                  <ButtonTransparentV2
                    icon={<MdEdit size="16px" />}
                    onClick={handleClickUpdateMintingDetails}
                    smart={false}
                  />
                )
              }
            />
          </Flex.Row>
        </PanelLayout$Emphasis.Main>
        <PanelLayout$Emphasis.Bottom>
          <Flex.Col gap="8px">
            <Flex.Row gap="4px" justifyContent="stretch">
              <Flex.Cell asChild flex="0 0 auto" minWidth="200px">
                <InputNumEditions$Compact.UseField$Natural
                  api={fieldNumEditions}
                  disabled={busy || stats.isSoldOut}
                />
              </Flex.Cell>
              <Flex.Cell asChild flex="1 1 auto">
                <ButtonPrimary
                  className={stats.isSoldOut ? styles.buttonSoldOut : undefined}
                  size="lg"
                  content={stats.isSoldOut ? "Sold Out" : "Purchase Now"}
                  disabled={busy || stats.isSoldOut}
                  onClick={handleClickMintNow}
                  smart={false}
                />
              </Flex.Cell>
            </Flex.Row>
            {!!fieldNumEditions.error && (
              <Flex.Cell>
                <InlineErrorViewer content={fieldNumEditions.error.message} />
              </Flex.Cell>
            )}
          </Flex.Col>
        </PanelLayout$Emphasis.Bottom>
      </PanelLayout$Emphasis.Root>
    </div>
  );
}
